import { KnownMembership, MatrixClient } from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { resolveWorkspaceTargetForRoom } from 'utils/roomHierarchyUtils';

/** Video-hub scheduled meeting the current user was invited to (not the organiser). */
export const isVideoHubGuestMeeting = (meeting: MeetingResponse, myUserId: string): boolean =>
  !!meeting.scheduled_at &&
  meeting.messenger_room_id == null &&
  meeting.cancelled_at == null &&
  meeting.created_by_mxid !== myUserId;

/** Guest meeting in personal workspace or a workspace the user has joined. */
export const isAccessibleVideoHubGuestMeeting = (
  meeting: MeetingResponse,
  myUserId: string,
  joinedWorkspaces: Record<string, unknown>,
  invitedMeetingRoomIds: ReadonlySet<string> = new Set()
): boolean => {
  if (!isVideoHubGuestMeeting(meeting, myUserId)) return false;
  const workspaceId = meeting.workspace_matrix_room_id;
  if (workspaceId == null || workspaceId in joinedWorkspaces) return true;
  // #8314 — A shared meeting created in a workspace carries that workspace's id even when
  // the invitee is not a member. An explicit Matrix invite/join on the meeting room is
  // sufficient access; do not require workspace membership in that case.
  return invitedMeetingRoomIds.has(meeting.matrix_room_id);
};

/** Target workspace for a video-hub meeting deep-link (null = personal). */
export const resolveMeetingWorkspaceTarget = (
  meeting: MeetingResponse | null | undefined,
  matrixClient: MatrixClient,
  roomId: string,
  joinedWorkspaces: Record<string, unknown>
): string | null | undefined => {
  if (meeting) {
    const workspaceId = meeting.workspace_matrix_room_id;
    if (workspaceId == null) return null;
    if (workspaceId in joinedWorkspaces) return workspaceId;
    return undefined;
  }

  return resolveWorkspaceTargetForRoom(matrixClient, roomId, joinedWorkspaces);
};

export const shouldSurfaceMeetingSpaceInvite = ({
  membership,
  wasDeferredInvite,
  isBackendGuest,
}: {
  membership: string | undefined;
  wasDeferredInvite: boolean;
  isBackendGuest: boolean;
}): boolean => {
  if (membership === KnownMembership.Leave || membership === KnownMembership.Ban) return false;
  if (membership === KnownMembership.Join && !wasDeferredInvite) return false;
  if (wasDeferredInvite) return true;
  if (membership === KnownMembership.Invite) return true;
  if (isBackendGuest) return true;
  return false;
};
